import { Link, useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { CheckCircle2, ArrowRight, Send, ExternalLink } from 'lucide-react';
import { lessonByPathAndSlug } from '../data/learningContent';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { generateAIResponse } from '../lib/aiClient';
import { toast } from 'sonner';
import { usePlan } from '../lib/usePlan';
import ToolSwitcher from '../components/ToolSwitcher';
import { ToolKey, getTool, deepLinkForTool } from '../data/tools';
import LessonShell from '../components/LessonShell';
import Beat from '../components/Beat';

const PATH_SKILL: Record<string, string> = {
  beginners: 'asking better questions',
  learners: 'thinking with AI',
  parents: 'guiding curiosity',
  daily: 'using AI in daily life',
  creative: 'creating with AI',
};

export default function LessonPage() {
  const { slug, lessonSlug } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const plan = usePlan();
  const found = slug && lessonSlug ? lessonByPathAndSlug(slug, lessonSlug) : null;

  const [prompt, setPrompt] = useState('');
  const [output, setOutput] = useState('');
  const [running, setRunning] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [tool, setTool] = useState<ToolKey>(getTool(profile?.preferred_tool).key);
  const [reflectAnswer, setReflectAnswer] = useState('');
  const [applyAnswer, setApplyAnswer] = useState('');
  const [savedReflect, setSavedReflect] = useState(false);
  const [savedApply, setSavedApply] = useState(false);

  useEffect(() => {
    if (profile?.preferred_tool) setTool(getTool(profile.preferred_tool).key);
  }, [profile?.preferred_tool]);

  useEffect(() => {
    if (!user || !lessonSlug) return;
    (async () => {
      const [{ data: prog }, { data: refs }] = await Promise.all([
        supabase
          .from('user_progress')
          .select('status')
          .eq('user_id', user.id)
          .eq('lesson_slug', lessonSlug)
          .maybeSingle(),
        supabase
          .from('reflections')
          .select('prompt_key, answer')
          .eq('user_id', user.id)
          .eq('lesson_slug', lessonSlug),
      ]);
      setCompleted(prog?.status === 'completed');
      const r = (refs ?? []).find((x: { prompt_key: string }) => x.prompt_key === 'reflect');
      const a = (refs ?? []).find((x: { prompt_key: string }) => x.prompt_key === 'apply');
      if (r) {
        setReflectAnswer(r.answer);
        setSavedReflect(true);
      }
      if (a) {
        setApplyAnswer(a.answer);
        setSavedApply(true);
      }
    })();
  }, [user, lessonSlug]);

  if (!found) {
    return (
      <div className="container-app py-16 max-w-xl">
        <p>Challenge not found.</p>
        <Link to="/skill-paths" className="btn-secondary mt-4">Back</Link>
      </div>
    );
  }
  const { path, module, lesson } = found;
  const moduleIdx = path.modules.findIndex((m) => m.slug === module.slug);
  const locked = !plan.loading && !plan.isPro && moduleIdx > 0;

  const allLessonsInPath = path.modules.flatMap((m) => m.lessons);
  const idx = allLessonsInPath.findIndex((l) => l.slug === lesson.slug);
  const next = allLessonsInPath[idx + 1];
  const skill = PATH_SKILL[path.slug] ?? 'AI fluency';

  const hookContent = lesson.content.filter((c) => c.type === 'concept' || c.type === 'example');
  const tryContent = lesson.content.filter((c) => c.type === 'do');
  const reflectContent = lesson.content.filter((c) => c.type === 'reflect');
  const suggestedPrompt = lesson.content.find((c) => c.prompt)?.prompt;

  if (locked) {
    return (
      <div className="container-app py-16 max-w-xl">
        <div className="card p-10 text-center">
          <h2 className="font-display text-3xl mb-2">This challenge is in a Pro series.</h2>
          <p className="text-ink-600 mb-6">The Starter plan includes the first series of every path. Upgrade for the full path.</p>
          <div className="flex justify-center gap-2">
            <Link to="/pricing" className="btn-primary">View plans</Link>
            <Link to={`/skill-paths/${path.slug}`} className="btn-secondary">Back to path</Link>
          </div>
        </div>
      </div>
    );
  }

  const runPrompt = async () => {
    if (!prompt.trim()) return;
    setRunning(true);
    try {
      const deepPaths = ['better-prompting', 'positioning', 'campaign-planning'];
      const tier: 'fast' | 'deep' = deepPaths.includes(path.slug) ? 'deep' : 'fast';
      const r = await generateAIResponse({ prompt, tier, tool });
      setOutput(r);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  const saveReflection = async (key: 'reflect' | 'apply', answer: string) => {
    if (!user || !answer.trim()) return;
    await supabase
      .from('reflections')
      .upsert(
        {
          user_id: user.id,
          lesson_slug: lesson.slug,
          path_slug: path.slug,
          prompt_key: key,
          answer: answer.trim(),
        },
        { onConflict: 'user_id,lesson_slug,prompt_key' as any },
      );
    if (key === 'reflect') setSavedReflect(true);
    else setSavedApply(true);
  };

  const completeLesson = async () => {
    if (!user) return;
    if (reflectAnswer.trim() && !savedReflect) await saveReflection('reflect', reflectAnswer);
    if (applyAnswer.trim() && !savedApply) await saveReflection('apply', applyAnswer);
    await supabase.from('user_progress').upsert(
      {
        user_id: user.id,
        lesson_slug: lesson.slug,
        skill_path_slug: path.slug,
        module_slug: module.slug,
        status: 'completed',
        completed_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,lesson_slug' },
    );
    setCompleted(true);
    toast.success('Challenge complete');
    if (next) navigate(`/skill-paths/${path.slug}/${next.slug}`);
    else navigate(`/skill-paths/${path.slug}`);
  };

  return (
    <LessonShell
      backTo={`/skill-paths/${path.slug}`}
      backLabel={path.title}
      objective={`By the end, you'll be able to: ${lesson.summary}`}
      title={lesson.title}
      skill={skill}
      duration={lesson.duration}
      meta={
        <div className="flex items-center gap-2">
          {completed && (
            <span className="inline-flex items-center gap-1 text-xs text-ink-700">
              <CheckCircle2 className="w-3.5 h-3.5" /> Completed
            </span>
          )}
        </div>
      }
    >
      <Beat kind="hook" number={1}>
        {hookContent.length === 0 && (
          <p className="text-ink-700">{lesson.summary}</p>
        )}
        {hookContent.map((c, i) => (
          <div key={i}>
            {c.title && <div className="font-display text-lg mb-1.5">{c.title}</div>}
            <p className="whitespace-pre-line leading-relaxed">{c.body}</p>
          </div>
        ))}
      </Beat>

      <Beat kind="try" number={2}>
        {tryContent.length > 0 ? (
          tryContent.map((c, i) => (
            <div key={i} className="mb-2">
              {c.title && <div className="font-medium text-ink-900 mb-1">{c.title}</div>}
              <p className="whitespace-pre-line">{c.body}</p>
              {c.prompt && (
                <button
                  onClick={() => setPrompt(c.prompt!)}
                  className="text-xs font-medium text-ink-700 hover:text-ink-900 inline-flex items-center gap-1 mt-2"
                >
                  Load this prompt <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </div>
          ))
        ) : (
          <p>Use the workspace to try this skill in your own words.</p>
        )}

        <div className="mt-4 border border-ink-100 rounded-xl p-4 bg-warm-50/50">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-ink-500">Workspace</div>
            <ToolSwitcher value={tool} onChange={setTool} />
          </div>
          {suggestedPrompt && !prompt && (
            <button
              onClick={() => setPrompt(suggestedPrompt)}
              className="text-xs text-ink-700 hover:text-ink-900 mb-2 inline-block"
            >
              Load suggested prompt
            </button>
          )}
          <textarea
            className="w-full bg-white border border-ink-200 rounded-lg p-3 text-sm focus:outline-none focus:border-ink-500"
            rows={6}
            placeholder="Type or paste a prompt to try with AI..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <button
            onClick={runPrompt}
            disabled={running || !prompt.trim()}
            className="btn-primary mt-3 disabled:opacity-50"
          >
            {running ? 'Generating...' : 'Run prompt'} <Send className="w-4 h-4" />
          </button>
          {output && (
            <div className="mt-4 border border-ink-100 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-2 gap-3">
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-ink-500">Output</div>
                <a
                  href={deepLinkForTool(tool)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(prompt);
                      toast.success('Prompt copied');
                    } catch {}
                  }}
                  className="text-xs text-ink-700 hover:text-ink-900 inline-flex items-center gap-1"
                >
                  Try in {getTool(tool).name} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="text-sm text-ink-700 leading-relaxed whitespace-pre-wrap">{output}</div>
            </div>
          )}
        </div>
      </Beat>

      <Beat kind="reflect" number={3}>
        {reflectContent.length > 0 ? (
          reflectContent.map((c, i) => (
            <p key={i} className="italic text-ink-700">
              {c.body}
            </p>
          ))
        ) : (
          <p className="italic text-ink-700">What worked when you tried it? What would you change next time?</p>
        )}
        <textarea
          className="w-full bg-white border border-ink-200 rounded-lg p-3 text-sm focus:outline-none focus:border-ink-500 mt-3"
          rows={3}
          placeholder="Write a sentence or two…"
          value={reflectAnswer}
          onChange={(e) => {
            setReflectAnswer(e.target.value);
            setSavedReflect(false);
          }}
          onBlur={() => reflectAnswer.trim() && saveReflection('reflect', reflectAnswer)}
        />
        {savedReflect && reflectAnswer && (
          <div className="text-xs text-ink-500 mt-1">Saved to your reflections</div>
        )}
      </Beat>

      <Beat kind="apply" number={4}>
        <p className="text-ink-700">
          Where else could you use this skill this week? Name one real situation — work, school, home, a hobby — and the
          first prompt you'd try.
        </p>
        <textarea
          className="w-full bg-white border border-ink-200 rounded-lg p-3 text-sm focus:outline-none focus:border-ink-500 mt-3"
          rows={3}
          placeholder="One real situation, one first prompt…"
          value={applyAnswer}
          onChange={(e) => {
            setApplyAnswer(e.target.value);
            setSavedApply(false);
          }}
          onBlur={() => applyAnswer.trim() && saveReflection('apply', applyAnswer)}
        />
        {savedApply && applyAnswer && (
          <div className="text-xs text-ink-500 mt-1">Saved to your reflections</div>
        )}
      </Beat>

      <div className="pt-4 flex flex-wrap items-center justify-between gap-4">
        <Link to={`/skill-paths/${path.slug}`} className="btn-secondary">
          Back to path
        </Link>
        <button onClick={completeLesson} className="btn-primary">
          {next ? 'Mark complete · Next challenge' : 'Mark complete'} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </LessonShell>
  );
}
